import {
  useAddETSTicketRatingMutation,
  useDeleteETSTicketRatingMutation,
} from 'gcs-common/clients/api/entities/tickets/tickets-api';
import { Button, ButtonSize, ButtonVariant } from 'gcs-common/design-system/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'gcs-common/design-system/ui/dialog';
import StarRating from 'gcs-common/design-system/ui/star-rating';
import { useState } from 'react';
import TicketRatingModal from './TicketRatingModal/TicketRatingModal';

interface TicketRatingProps {
  ticketId: string;
  comment?: string;
  rating?: number;
}

const TicketRating = (props: TicketRatingProps) => {
  const { ticketId, comment, rating } = props;

  const [openModal, setOpenModal] = useState(false);

  const addETSTicketRating = useAddETSTicketRatingMutation();
  const deleteETSTicketRating = useDeleteETSTicketRatingMutation();
  const isAddLoading = addETSTicketRating.isPending;
  const isDeleteLoading = deleteETSTicketRating.isPending;

  const handleRating = async (newRating: number, newComment: string) => {
    try {
      await addETSTicketRating.mutateAsync({
        path: {
          ticket_id: ticketId,
        },
        body: {
          rating: newRating,
          comment: newComment,
        },
      });
    } catch (_e) {
      // Nothing needed
    }
    setOpenModal(false);
  };

  const handleDelete = async () => {
    try {
      await deleteETSTicketRating.mutateAsync({
        path: {
          ticket_id: ticketId,
        },
      });
    } catch (_e) {
      // Nothing needed
    }
    setOpenModal(false);
  };

  const handleCancel = () => setOpenModal(false);

  return (
    <div className="mb-2 flex justify-start">
      <div className="relative max-w-[50%] break-words rounded-t-2xl rounded-r-2xl bg-white px-3 pt-2 pb-3 text-neutral-11 text-sm shadow-md ring-1 ring-neutral-3">
        <Dialog open={openModal} onOpenChange={setOpenModal}>
          <div className="font-semibold text-accent-9 text-sm">Ersatzteil-Team</div>
          {!rating ? (
            <div>
              <p className="mt-1 mb-3"> Wie zufrieden warst du mit der Bearbeitung dieser Anfrage? </p>
              <DialogTrigger asChild>
                <Button type="button" size={ButtonSize.l} className="w-full" variant={ButtonVariant.solid_accent}>
                  Bewerten
                </Button>
              </DialogTrigger>
            </div>
          ) : (
            <div className="pb-2">
              <p>Danke für deine Rückmeldung zur Zufriedenheit!</p>
              <StarRating ratingValue={rating} />
              {comment && (
                <div className="mt-2 mb-4 whitespace-pre-line break-words rounded-lg bg-neutral-3 p-2">{comment}</div>
              )}
              <DialogTrigger asChild>
                <Button type="button" size={ButtonSize.m} variant={ButtonVariant.soft_neutral}>
                  Bearbeiten
                </Button>
              </DialogTrigger>
            </div>
          )}
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="my-0 font-medium text-xl">Wie haben wir uns geschlagen?</DialogTitle>
              <DialogDescription className="text-neutral-11">
                Hinterlasse eine Bewertung und einen Kommentar zu dieser Ersatzteilanfrage.
              </DialogDescription>
            </DialogHeader>
            <TicketRatingModal
              rating={rating ?? 0}
              comment={comment ?? ''}
              handleRating={handleRating}
              handleCancel={handleCancel}
              handleDelete={handleDelete}
              open={openModal}
              isAddLoading={isAddLoading}
              isDeleteLoading={isDeleteLoading}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TicketRating;
